import { useState } from "react";
import { api, type HealthCheck, type ProductRow } from "../api";
import { useAsync } from "../useAsync";
import { shortTime, DASH } from "../format";

const RANGES = [7, 30, 90];

/**
 * The product audit, in the spirit of an SEO site-health report: one score,
 * four categories (site & tracking, funnel, advertising, SEO & social), and a
 * fix-first list where every finding carries the action that clears it.
 * Deterministic checks over the warehouse — no vibes in the score.
 */
export function HealthReport({ product }: { product: ProductRow }) {
  const [days, setDays] = useState(30);
  const report = useAsync(() => api.healthReport(product.slug, days), [product.slug, days]);

  if (report.error) return <PageProblem message={report.error} />;
  if (report.loading || !report.data) return <PageLoading />;
  const r = report.data;

  const all = r.categories.flatMap((c) => c.checks.map((ch) => ({ ...ch, category: c.label })));
  const failing = all.filter((c) => c.status === "fail");
  const warning = all.filter((c) => c.status === "warn");
  const passing = all.filter((c) => c.status === "pass");
  const skipped = all.filter((c) => c.status === "skip");

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>Health</h1>
          <span className="sub">
            A scored audit of {product.name}: tracking, funnel, advertising, SEO &amp; social — and what to fix first.
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((d) => (
            <button key={d} type="button" className="seg" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* --------------------------------------------- score + categories */}
      <div style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap", padding: "28px 0 24px",
                    borderBottom: "1px solid var(--border)" }}>
        <ScoreDial score={r.score} grade={r.grade} />
        <div style={{ flex: 1, minWidth: 280, display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14 }}>
          {r.categories.map((c) => {
            const issues = c.checks.filter((ch) => ch.status === "fail" || ch.status === "warn").length;
            return (
              <div key={c.key} className="panel" style={{ padding: "16px 18px 14px" }}>
                <div className="eyebrow">{c.label}</div>
                <div className="num" style={{ fontSize: 24, letterSpacing: "-0.03em", marginTop: 7,
                                              color: scoreColor(c.score) }}>
                  {c.score === null ? DASH : c.score}
                </div>
                <div style={{ height: 4, background: "var(--surface-inset)", borderRadius: 2, marginTop: 9 }}>
                  {c.score !== null && (
                    <div style={{ height: "100%", width: `${c.score}%`, borderRadius: 2, background: scoreColor(c.score) }} />
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: issues > 0 ? "var(--warn)" : "var(--text-faint)", marginTop: 8 }}>
                  {c.score === null ? "not set up yet" : issues > 0 ? `${issues} to fix` : "all clear"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ------------------------------------------------------ fix first */}
      <Section
        title="Fix first"
        note={failing.length + warning.length === 0
          ? undefined
          : "Ordered by severity. Each finding carries the action that clears it."}
      >
        {failing.length + warning.length === 0 ? (
          <Empty>Nothing needs fixing in this window. The Keeper keeps checking daily.</Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {[...failing, ...warning].map((c) => <CheckRow key={`${c.category}:${c.id}`} check={c} showAction />)}
          </div>
        )}
      </Section>

      {skipped.length > 0 && (
        <Section title="Not set up yet" note="Unscored until the source exists — each row says how to switch it on.">
          <div style={{ display: "flex", flexDirection: "column" }}>
            {skipped.map((c) => <CheckRow key={`${c.category}:${c.id}`} check={c} showAction />)}
          </div>
        </Section>
      )}

      {passing.length > 0 && (
        <Section title={`Passing (${passing.length})`}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {passing.map((c) => <CheckRow key={`${c.category}:${c.id}`} check={c} />)}
          </div>
        </Section>
      )}

      <div className="banner" style={{ marginTop: 26 }}>
        <span>
          Generated {shortTime(r.generated_at)} over a {r.window_days}-day window. Deterministic checks on
          warehouse data — reconnect or fix, then Refresh to re-score.
        </span>
      </div>
    </div>
  );
}

function CheckRow({ check, showAction }: { check: HealthCheck & { category: string }; showAction?: boolean }) {
  const color = check.status === "pass" ? "var(--pos)" : check.status === "warn" ? "var(--warn)"
    : check.status === "fail" ? "var(--neg)" : "var(--muted)";
  return (
    <div style={{ display: "flex", gap: 14, padding: "13px 2px", borderBottom: "1px solid var(--border-hairline)" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flex: "none", marginTop: 6 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13.5, fontWeight: 500 }}>{check.label}</span>
          <span className="eyebrow">{check.category}</span>
        </div>
        <div className="num" style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 3 }}>{check.evidence}</div>
        {showAction && check.action && (
          <div style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 6, paddingLeft: 10,
                        borderLeft: "2px solid var(--border-strong)", lineHeight: 1.5 }}>
            {check.action}
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreDial({ score, grade }: { score: number | null; grade: string | null }) {
  const R = 56;
  const C = 2 * Math.PI * R;
  const frac = score === null ? 0 : score / 100;
  const color = scoreColor(score);
  return (
    <div style={{ position: "relative", width: 148, height: 148, flex: "none" }}>
      <svg viewBox="0 0 148 148" style={{ width: 148, height: 148, transform: "rotate(-90deg)" }} role="img"
           aria-label={score === null ? "No score yet" : `Health score ${score} out of 100`}>
        <circle cx="74" cy="74" r={R} fill="none" stroke="var(--surface-inset)" strokeWidth="11" />
        {score !== null && (
          <circle cx="74" cy="74" r={R} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
                  strokeDasharray={`${C * frac} ${C}`} />
        )}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 2 }}>
        <span className="num" style={{ fontSize: 34, letterSpacing: "-0.04em", color: score === null ? "var(--text-faint)" : "var(--text)" }}>
          {score === null ? DASH : score}
        </span>
        <span style={{ fontSize: 11, color, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {grade ?? "no data yet"}
        </span>
      </div>
    </div>
  );
}

function scoreColor(score: number | null): string {
  if (score === null) return "var(--muted)";
  return score >= 70 ? "var(--pos)" : score >= 50 ? "var(--warn)" : "var(--neg)";
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 30 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 8 }}>
        <h2>{title}</h2>
        {note && <span className="sub" style={{ maxWidth: 720 }}>{note}</span>}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 12, padding: "20px 22px",
                  fontSize: 13, lineHeight: 1.6, color: "var(--text-3)", background: "var(--surface)" }}>
      {children}
    </div>
  );
}

function PageLoading() {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--text-faint)" }}>Loading…</div>;
}
function PageProblem({ message }: { message: string }) {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--neg)" }}>{message}</div>;
}
